import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatEuros } from "@sundial/core";
import { useImportDays, useImportPayments, type DaysImportPreview, type PaymentsImportPreview } from "../api/queries.js";

type ImportType = "days" | "payments";

/**
 * The single source of truth for the CSV format, surfaced as an ⓘ popover on
 * every import (there is one ImportCard, reused everywhere). Editing the format
 * means editing the importCard.* i18n keys here — the popover stays in sync.
 */
function FormatInfo({ type }: { type: ImportType }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const required = t(type === "payments" ? "importCard.paymentsRequired" : "importCard.daysRequired");
  const optional = t(type === "payments" ? "importCard.paymentsOptional" : "importCard.daysOptional");
  const note = t(type === "payments" ? "importCard.paymentsNote" : "importCard.daysNote");
  const example = t(type === "payments" ? "importCard.paymentsExample" : "importCard.daysExample");

  return (
    <span style={{ position: "relative", flex: "none" }}>
      <button
        type="button"
        aria-label={t("importCard.formatTrigger")}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{ display: "inline-flex", alignItems: "center", gap: 5, height: 26, padding: "0 9px", borderRadius: 8, border: "1px solid var(--border)", background: open ? "var(--surface)" : "var(--card)", color: "var(--muted)", cursor: "pointer", font: "600 11px Sora" }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8h.01M11 12h1v4h1" />
        </svg>
        {t("importCard.formatTrigger")}
      </button>
      {open && (
        <>
          {/* click-away layer */}
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div
            role="dialog"
            style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 41, width: 320, maxWidth: "80vw", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, boxShadow: "0 8px 30px rgba(15,26,48,.18)", padding: 14 }}
          >
            <div style={{ font: "700 12px Sora", color: "var(--text)", marginBottom: 9 }}>{t("importCard.formatTitle")}</div>
            <div style={{ fontSize: 12, color: "var(--text)", marginBottom: 4 }}>{required}</div>
            {optional && <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>{optional}</div>}
            {note && <div style={{ fontSize: 11.5, color: "var(--muted2)", lineHeight: 1.45, marginBottom: 9 }}>{note}</div>}
            <pre style={{ margin: 0, padding: "9px 11px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--text)", overflowX: "auto", whiteSpace: "pre" }}>
              {example}
            </pre>
          </div>
        </>
      )}
    </span>
  );
}

const cardStyle: React.CSSProperties = {
  background: "var(--card)",
  border: "1px solid var(--border)",
  borderRadius: 16,
  boxShadow: "0 1px 2px rgba(15,26,48,.04),0 6px 18px rgba(15,26,48,.04)",
  padding: 18,
  marginBottom: 14,
};

const labelStyle: React.CSSProperties = {
  font: "600 11px/14px Sora",
  letterSpacing: ".07em",
  textTransform: "uppercase",
  color: "var(--muted2)",
  marginBottom: 10,
};

function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        height: 38,
        padding: "0 15px",
        borderRadius: 10,
        cursor: "pointer",
        font: "600 13px Sora",
        background: active ? "var(--primary)" : "var(--card)",
        color: active ? "#fff" : "var(--text)",
        border: active ? "1px solid var(--primary)" : "1px solid var(--border)",
      }}
    >
      {children}
    </button>
  );
}

export interface ImportCardProps {
  /** Lock the card to a single import type and hide the type selector. */
  only?: ImportType;
  title?: string;
}

export function ImportCard({ only, title }: ImportCardProps = {}) {
  const { t } = useTranslation();
  const [type, setType] = useState<ImportType>(only ?? "days");
  const [file, setFile] = useState<File | null>(null);
  const [daysPreview, setDaysPreview] = useState<DaysImportPreview | null>(null);
  const [payPreview, setPayPreview] = useState<PaymentsImportPreview | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const importDays = useImportDays();
  const importPayments = useImportPayments();

  function reset() {
    setFile(null);
    setDaysPreview(null);
    setPayPreview(null);
    setDone(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  function switchType(t: ImportType) {
    setType(t);
    reset();
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setDone(null);
    setError(null);
    setDaysPreview(null);
    setPayPreview(null);
    if (!f) return;
    try {
      if (type === "days") setDaysPreview(await importDays.mutateAsync({ file: f, dryRun: true }));
      else setPayPreview(await importPayments.mutateAsync({ file: f, dryRun: true }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    }
  }

  async function confirm() {
    if (!file) return;
    try {
      if (type === "days") {
        const res = await importDays.mutateAsync({ file, dryRun: false });
        setDone(`Imported ${res.totalToAdd} worked day(s) across ${res.months.length} month(s).`);
      } else {
        const res = await importPayments.mutateAsync({ file, dryRun: false });
        setDone(`Imported ${res.created} payment(s).`);
      }
      setDaysPreview(null);
      setPayPreview(null);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
    }
  }

  const hasErrors =
    (daysPreview?.rowErrors.length ?? 0) > 0 && (daysPreview?.months.length ?? 0) === 0;
  const payHasErrors = (payPreview?.rowErrors.length ?? 0) > 0 && (payPreview?.rows ?? 0) === 0;
  const canConfirm = Boolean(file) && !hasErrors && !payHasErrors && (daysPreview !== null || payPreview !== null);

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, position: "relative" }}>
        <div style={labelStyle}>{title ?? t("importCard.titleDays")}</div>
        <FormatInfo type={type} />
      </div>
      {!only && (
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <Pill active={type === "days"} onClick={() => switchType("days")}>
            {t("reports.workedDays")}
          </Pill>
          <Pill active={type === "payments"} onClick={() => switchType("payments")}>
            {t("reports.payments")}
          </Pill>
        </div>
      )}
      <div style={{ fontSize: 12.5, color: "var(--muted)", marginBottom: 12 }}>
        {type === "payments" ? t("importCard.paymentsHint") : t("importCard.daysHint")}
      </div>

      <input
        ref={inputRef}
        data-testid="import-file"
        type="file"
        accept=".csv,text/csv"
        onChange={onFile}
        style={{ marginBottom: 14, color: "var(--text)" }}
      />

      {error && (
        <div data-testid="import-error" style={{ color: "#dc2626", fontSize: 13, marginBottom: 10 }}>{error}</div>
      )}

      {done && (
        <div data-testid="import-done" style={{ color: "var(--primary)", fontSize: 13, marginBottom: 10 }}>{done}</div>
      )}

      {/* Worked-days preview */}
      {daysPreview && (
        <div style={{ marginBottom: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                {["Month", "Target", "Existing", "Add"].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "6px 8px", font: "600 10px Sora", textTransform: "uppercase", color: "var(--muted2)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {daysPreview.months.map((m) => (
                <tr key={m.month} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ padding: "6px 8px", fontFamily: "'JetBrains Mono', monospace" }}>{m.month}</td>
                  <td style={{ padding: "6px 8px" }}>{m.target}</td>
                  <td style={{ padding: "6px 8px" }}>{m.existing}</td>
                  <td style={{ padding: "6px 8px", color: "var(--primary)" }}>+{m.toAdd}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {daysPreview.months.flatMap((m) => m.warnings).map((w, i) => (
            <div key={i} style={{ color: "#b45309", fontSize: 12, marginTop: 6 }}>⚠ {w}</div>
          ))}
          {daysPreview.rowErrors.map((e, i) => (
            <div key={i} style={{ color: "#dc2626", fontSize: 12, marginTop: 6 }}>Line {e.line}: {e.message}</div>
          ))}
        </div>
      )}

      {/* Payments preview */}
      {payPreview && (
        <div style={{ marginBottom: 12, fontSize: 13 }}>
          <div>{payPreview.rows} payment(s), total {formatEuros(payPreview.totalCents)}</div>
          {payPreview.rowErrors.map((e, i) => (
            <div key={i} style={{ color: "#dc2626", fontSize: 12, marginTop: 6 }}>Line {e.line}: {e.message}</div>
          ))}
        </div>
      )}

      {(daysPreview || payPreview) && (
        <button
          data-testid="import-confirm"
          disabled={!canConfirm}
          onClick={confirm}
          style={{
            height: 44,
            width: "100%",
            border: "none",
            borderRadius: 10,
            background: "var(--primary)",
            color: "#fff",
            font: "600 14px Sora",
            cursor: canConfirm ? "pointer" : "not-allowed",
            opacity: canConfirm ? 1 : 0.45,
          }}
        >
          Confirm import
        </button>
      )}
    </div>
  );
}
